import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Sparkles, Cpu } from "lucide-react";
import { toast } from "sonner";

import TopNav from "@/components/TopNav";
import LlmKeysPanel from "@/components/LlmKeysPanel";
import {
  llmProviders,
  llmGlobalKeys,
  llmGlobalSave,
  llmGlobalDelete,
  llmGlobalCheck,
} from "@/lib/api";

export default function AdminLlmKeys() {
  const [providers, setProviders] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      setLoading(true);
      const [pr, ks] = await Promise.all([llmProviders(), llmGlobalKeys()]);
      setProviders(pr.providers || []);
      setRows(ks.keys || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo cargar el catálogo LLM.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const onSave = async ({ provider, api_key, model }) => {
    await llmGlobalSave(provider, { api_key, model });
    await load();
  };
  const onDelete = async ({ provider }) => {
    await llmGlobalDelete(provider);
    await load();
  };
  const onCheck = async ({ provider, api_key, model }) => {
    const r = await llmGlobalCheck(provider, { api_key, model });
    await load();
    return r;
  };

  return (
    <div className="min-h-screen bg-white text-slate-900">
      <TopNav />
      <div className="mx-auto max-w-6xl px-6 py-12 sm:py-16">
        <p className="eyebrow text-accent-blue flex items-center gap-1.5">
          <Sparkles className="h-3 w-3" /> Administración · Modelos LLM
        </p>
        <h1 className="mt-2 font-display text-4xl text-slate-900 sm:text-5xl">
          Claves API por defecto
        </h1>
        <p className="mt-3 max-w-2xl text-sm text-slate-600">
          Estas claves se aplicarán a <strong>todos los agentes IA</strong> de la plataforma a menos que
          un agente tenga configurado un modelo propio. Las claves se cifran (AES-128) antes de
          almacenarse. Si no se configura ninguna, el sistema usa la clave universal de Inviktus (Claude Sonnet 4.5) como respaldo.
        </p>

        <div className="mt-6 grid gap-3 sm:flex sm:items-center sm:gap-4 text-[11px] uppercase tracking-[0.18em] text-slate-500">
          <Link to="/admin/planes" className="hover:text-slate-900">Planes</Link>
          <span className="text-slate-300">·</span>
          <Link to="/admin/facturacion" className="hover:text-slate-900">Facturación</Link>
          <span className="text-slate-300">·</span>
          <Link to="/admin/kbl" className="hover:text-slate-900">Knowledge Base</Link>
          <span className="text-slate-300">·</span>
          <span className="inline-flex items-center gap-1 font-medium text-accent-blue">
            <Cpu className="h-3.5 w-3.5" /> Modelos LLM
          </span>
        </div>

        <div className="mt-10">
          {loading ? (
            <div className="flex items-center justify-center py-24 text-slate-500" data-testid="llm-admin-loading">
              <Loader2 className="mr-2 h-5 w-5 animate-spin" />
              Cargando proveedores…
            </div>
          ) : (
            <LlmKeysPanel
              providers={providers}
              rows={rows}
              onSave={onSave}
              onDelete={onDelete}
              onCheck={onCheck}
              mode="global"
              title="Proveedores soportados"
              subtitle={`${providers.length} proveedores · prioridad de uso: override del agente → default global → clave universal Inviktus.`}
            />
          )}
        </div>
      </div>
    </div>
  );
}
